/**
 * Holds the page header a routed view registered, for the masthead to render.
 *
 * A component and nothing else, so Vite can hot-swap it: the channel it provides —
 * the context, the state shape, and the usePageHeader / useRegisteredPageHeader
 * hooks — lives in lib/pageHeader, because a module that mixes components with
 * plain functions loses fast refresh. See that file for why views register a
 * header at all rather than the masthead looking one up by route.
 *
 * It wraps the WHOLE shell, masthead included: the header is set by a view inside
 * <Routes> and read by AppHeader, which is their sibling, so the provider has to
 * sit above both.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { PageHeaderContext, type PageHeaderState } from '@/lib/pageHeader';

/** Hold the registered header for the shell. Wraps both the masthead and the routes. */
export default function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeaderState] = useState<PageHeaderState | null>(null);
  const setHeader = useCallback((next: PageHeaderState | null) => setHeaderState(next), []);
  const value = useMemo(() => ({ header, setHeader }), [header, setHeader]);
  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}
